import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import CurrentProfileSummary from "@/components/dashboard/CurrentProfileSummary";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import LearnerPortalShell from "@/components/learner/LearnerPortalShell";
import EmptyStateCdlPrompt from "@/components/dashboard/EmptyStateCdlPrompt";
import ProgressPanel from "@/components/dashboard/ProgressPanel";
import RuleDiffBrowsePanel from "@/components/dashboard/RuleDiffBrowsePanel";
import RuleDiffPanel, {
  type RuleDiffFetcher,
} from "@/components/dashboard/RuleDiffPanel";
import StateSwitcher from "@/components/dashboard/StateSwitcher";
import TrialExpiredPaywall from "@/components/paywall/TrialExpiredPaywall";
import ExamHistoryPanel from "@/components/exam/ExamHistoryPanel";
import { useEntitlement } from "@/entitlement/EntitlementGateContext";
import { getExamHistory, type ExamHistoryEntry } from "@/lib/exam-api";
import {
  ruleDiffCacheKey,
  useRuleDiffCache,
} from "@/hooks/useRuleDiffCache";
import { onSignInConsent, track } from "@/lib/analytics";
import { ApiError } from "@/lib/api";
import { getProgress } from "@/lib/practice-api";
import { getProfile, putProfile, type UserProfile } from "@/lib/profile-api";
import { generateRuleDiffForProfile } from "@/lib/rulediff-api";
import type { UsStateCode } from "@/lib/states";

/**
 * apps/web :: pages :: DashboardPage
 *
 * The /dashboard route. Closes the registration → profile → rule-diff →
 * state-switcher loop. Mounted inside <ProtectedRoute><OnboardingGate> in
 * App.tsx so a profile exists and onboarding is complete by the time this
 * renders.
 *
 * Wiring:
 *   1. Fetch the full UserProfile on mount (the gate only called the
 *      onboarding-status probe — we still need the real row for targets).
 *   2. Maintain `activeState` as local state, seeded from
 *      `profile.current_focus_state`. The StateSwitcher drives this.
 *   3. Build a memoised fetcher around `generateRuleDiffForProfile` + the
 *      session cache so the RuleDiffPanel's auto-fetch hits the cache on
 *      repeat visits.
 *   4. On tab switch: optimistically update local state + kick off a
 *      best-effort PUT to persist `current_focus_state`. PUT failures are
 *      logged and swallowed — the UI remains on the user's selection.
 *
 * Edge case: if getProfile returns null somehow (OnboardingGate should
 * prevent this) we redirect to /onboarding rather than render broken UI.
 */

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; profile: UserProfile; activeState: UsStateCode }
  | { kind: "no-profile" }
  | { kind: "error"; message: string };

export default function DashboardPage() {
  const { t } = useTranslation();
  const { isExpired } = useEntitlement();
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [examHistory, setExamHistory] = useState<ExamHistoryEntry[]>([]);
  const cache = useRuleDiffCache();
  // StrictMode idempotency: React 18 double-invokes effects in dev, so
  // the obvious `useEffect(() => track(...))` pattern would emit twice.
  // Pin dashboard.page_viewed + onSignInConsent to a single call per mount
  // via a ref that survives the dev re-invocation (refs live across the
  // double-effect, useState would reset).
  const pageViewedFiredRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const profile = await getProfile();
        if (cancelled) return;
        if (profile === null) {
          setState({ kind: "no-profile" });
          return;
        }
        // current_focus_state MUST be non-null for an onboarded user (the
        // wizard enforces this). If it's null we fall back to the first
        // target state rather than crashing.
        const focus = profile.current_focus_state ?? profile.target_states[0];
        if (!focus) {
          setState({ kind: "no-profile" });
          return;
        }
        setState({ kind: "ready", profile, activeState: focus });
        // Analytics: authenticated user has landed on the first product
        // surface. Implicit consent fires here (ROADS-307 MVP; follow-up
        // for explicit consent UI) + dashboard.page_viewed, both guarded
        // against StrictMode's double-effect by the ref.
        if (!pageViewedFiredRef.current) {
          pageViewedFiredRef.current = true;
          onSignInConsent();
          track("dashboard.page_viewed", { current_focus_state: focus });
        }
      } catch (err) {
        if (cancelled) return;
        const message =
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : String(err);
        setState({ kind: "error", message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Build the fetcher lazily — it depends on the cache instance, which is
  // stable across renders, so the memo result is effectively stable too.
  const fetcher: RuleDiffFetcher = useCallback(
    (profile, currentState) => {
      const key = ruleDiffCacheKey(
        profile.home_country,
        currentState,
        profile.native_language,
      );
      return cache.fetchOrGet(key, () =>
        generateRuleDiffForProfile(profile, currentState),
      );
    },
    [cache],
  );

  const handleSelect = useCallback((next: UsStateCode) => {
    setState((prev) => {
      if (prev.kind !== "ready") return prev;
      if (prev.activeState === next) return prev;
      // Emit the state-switch event BEFORE the optimistic state update so
      // `from_state` reflects what the user was looking at, not what
      // they're about to see.
      track("dashboard.state_switched", {
        from_state: prev.activeState,
        to_state: next,
      });
      const updated: LoadState = {
        ...prev,
        profile: { ...prev.profile, current_focus_state: next },
        activeState: next,
      };
      // Fire-and-forget persistence. Failures are logged but never break
      // the optimistic UI — per the ticket spec, next session will resync
      // from the server.
      void putProfile({
        home_country: prev.profile.home_country,
        native_language: prev.profile.native_language,
        target_states: prev.profile.target_states,
        current_focus_state: next,
        cdl_interest: prev.profile.cdl_interest,
      }).catch((err) => {
        // Intentionally warn-level, not error-level — this path is
        // recoverable on the next session.
        // eslint-disable-next-line no-console
        console.warn("DashboardPage: putProfile(current_focus_state) failed", err);
      });
      return updated;
    });
  }, []);

  // Fetch a short history of exam attempts for the active state. Best-effort:
  // failures are swallowed silently (the banner simply doesn't render), the
  // main dashboard story doesn't depend on exam mode being configured.
  useEffect(() => {
    if (state.kind !== "ready") return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await getExamHistory(state.activeState, 5);
        if (!cancelled) setExamHistory(res.attempts);
      } catch {
        if (!cancelled) setExamHistory([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [state]);

  const cdlInterest = useMemo(() => {
    if (state.kind !== "ready") return false;
    return state.profile.cdl_interest === true;
  }, [state]);

  if (state.kind === "loading") {
    return (
      <LearnerPortalShell pageTitle={t("dashboard.title")}>
        <div
          role="status"
          aria-live="polite"
          data-testid="dashboard-loading"
          className="mx-auto mt-xl max-w-md p-md text-center text-neutral-700"
        >
          {t("dashboard.loading")}
        </div>
      </LearnerPortalShell>
    );
  }

  if (state.kind === "no-profile") {
    return <Navigate to="/onboarding" replace />;
  }

  if (state.kind === "error") {
    return (
      <LearnerPortalShell pageTitle={t("dashboard.title")}>
        <div
          role="alert"
          data-testid="dashboard-load-error"
          className="mx-auto mt-xl max-w-md rounded-md border border-danger/40 bg-danger/5 p-md text-sm text-danger"
        >
          {t("dashboard.loadError", { message: state.message })}
        </div>
      </LearnerPortalShell>
    );
  }

  const { profile, activeState } = state;

  // PROFILE-PAYWALL: when the trial / paid access has lapsed, the
  // dashboard body becomes the conversion surface. We replace the
  // standard rule-diff + practice + exam panels with the focused
  // <TrialExpiredPaywall> card, which carries an EN-only language toggle
  // (with native-language script labels) and a single CTA to /pricing.
  // Nav, profile-summary, and state-switcher remain visible so the user
  // can still review their selected target state and dispatch upgrade
  // intent without losing context.
  if (isExpired) {
    return (
      <LearnerPortalShell pageTitle={t("dashboard.title")}>
        <DashboardLayout
          summary={<CurrentProfileSummary profile={profile} />}
          switcher={null}
          main={
            <TrialExpiredPaywall nativeLanguage={profile.native_language} />
          }
        />
      </LearnerPortalShell>
    );
  }

  return (
    <LearnerPortalShell pageTitle={t("dashboard.title")}>
      <DashboardLayout
        summary={<CurrentProfileSummary profile={profile} />}
        switcher={
          <StateSwitcher
            targetStates={profile.target_states}
            activeState={activeState}
            onSelect={handleSelect}
          />
        }
        main={
          <>
            <RuleDiffPanel
              profile={profile}
              currentState={activeState}
              fetcher={fetcher}
            />
            <div className="mt-lg">
              <RuleDiffBrowsePanel
                profile={profile}
                currentState={activeState}
              />
            </div>
            <div className="mt-lg">
              <section
                data-testid="dashboard-exam-cta"
                aria-labelledby="dashboard-exam-cta-heading"
                className="rounded-lg border border-brand-primary/10 bg-white p-md shadow-sm sm:p-lg"
              >
                <h2
                  id="dashboard-exam-cta-heading"
                  className="font-display text-xl font-bold text-brand-primary"
                >
                  {t("exam.dashboardCta.heading")}
                </h2>
                <p className="mt-sm text-sm text-neutral-800">
                  {t("exam.dashboardCta.body", { state: activeState })}
                </p>
                <Link
                  to={`/exam/${activeState}`}
                  data-testid="dashboard-exam-cta-link"
                  className="mt-md inline-flex items-center rounded-md border border-brand-primary bg-brand-primary px-md py-sm text-sm font-semibold text-white no-underline transition-colors hover:bg-brand-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
                >
                  {t("exam.dashboardCta.link")}
                </Link>
                {/*
                  Wave 15b: subtle secondary link to the dedicated
                  /exam/history page. Lives under the primary CTA so
                  the dashboard's main story (start an exam) keeps the
                  visual lead.
                */}
                <p className="mt-sm text-xs">
                  <Link
                    to="/exam/history"
                    data-testid="dashboard-exam-history-link"
                    className="text-brand-primary underline underline-offset-2 hover:no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
                  >
                    {t("dashboard.examHistoryLink")}
                  </Link>
                </p>
              </section>
            </div>
            <div className="mt-lg">
              <section
                data-testid="dashboard-training-cta"
                aria-labelledby="dashboard-training-cta-heading"
                className="rounded-lg border border-teal-200 bg-gradient-to-br from-teal-50 to-emerald-50 p-md shadow-sm sm:p-lg"
              >
                <div className="flex flex-wrap items-center gap-md">
                  <div
                    aria-hidden="true"
                    className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-teal-100 text-2xl"
                  >
                    🎓
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2
                      id="dashboard-training-cta-heading"
                      className="font-display text-lg font-bold text-teal-900"
                    >
                      {t("dashboard.trainingCta.heading")}
                    </h2>
                    <p className="mt-xs text-sm text-teal-800">
                      {t("dashboard.trainingCta.body")}
                    </p>
                  </div>
                  <Link
                    to="/training"
                    data-testid="dashboard-training-cta-link"
                    className="shrink-0 rounded-full bg-teal-600 px-md py-sm text-sm font-semibold text-white no-underline transition-colors hover:bg-teal-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-700"
                  >
                    {t("dashboard.trainingCta.link")}
                  </Link>
                </div>
              </section>
            </div>
            <div className="mt-lg">
              <section
                data-testid="dashboard-apply-cta"
                aria-labelledby="dashboard-apply-cta-heading"
                className="rounded-lg border border-amber-200 bg-gradient-to-br from-amber-50 to-yellow-50 p-md shadow-sm sm:p-lg"
              >
                <div className="flex flex-wrap items-center gap-md">
                  <div
                    aria-hidden="true"
                    className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-amber-100 text-2xl"
                  >
                    📋
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2
                      id="dashboard-apply-cta-heading"
                      className="font-display text-lg font-bold text-amber-900"
                    >
                      {t("dashboard.applyCta.heading", { state: activeState })}
                    </h2>
                    <p className="mt-xs text-sm text-amber-800">
                      {t("dashboard.applyCta.body")}
                    </p>
                  </div>
                  <Link
                    to={`/apply/${activeState}`}
                    data-testid="dashboard-apply-cta-link"
                    className="shrink-0 rounded-full bg-amber-600 px-md py-sm text-sm font-semibold text-white no-underline transition-colors hover:bg-amber-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-700"
                  >
                    {t("dashboard.applyCta.link")}
                  </Link>
                </div>
              </section>
            </div>
            <div className="mt-lg">
              <ProgressPanel state={activeState} fetcher={getProgress} />
            </div>
            {examHistory.length > 0 && (
              <ExamHistoryPanel
                stateCode={activeState}
                attempts={examHistory}
              />
            )}
            {/*
              Wave 12 Pillar B: dashboard entry into the CDL surface. Shown
              to every onboarded learner (the assumption being that any
              authed user already holds — or is studying for — a Class C
              licence) with extra emphasis when they explicitly flagged
              CDL interest during onboarding. The link points at Texas
              (the first fully-shipped CDL state) and the CDL info page
              gracefully degrades for non-TX state codes.
            */}
            <div className="mt-lg">
              <section
                data-testid="dashboard-cdl-cta"
                aria-labelledby="dashboard-cdl-cta-heading"
                className="rounded-lg border border-indigo-200 bg-gradient-to-br from-indigo-50 to-purple-50 p-md shadow-sm sm:p-lg"
              >
                <div className="flex flex-wrap items-center gap-md">
                  <div
                    aria-hidden="true"
                    className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-2xl"
                  >
                    🚛
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2
                      id="dashboard-cdl-cta-heading"
                      className="font-display text-lg font-bold text-indigo-900"
                    >
                      {t("dashboard.cdlCta.heading")}
                    </h2>
                    <p className="mt-xs text-sm text-indigo-800">
                      {t("dashboard.cdlCta.body")}
                    </p>
                  </div>
                  <Link
                    to="/cdl/license/TX"
                    data-testid="dashboard-cdl-cta-link"
                    className="shrink-0 rounded-full bg-indigo-600 px-md py-sm text-sm font-semibold text-white no-underline transition-colors hover:bg-indigo-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-700"
                  >
                    {t("dashboard.cdlCta.link")}
                  </Link>
                </div>
              </section>
            </div>
            <EmptyStateCdlPrompt cdlInterest={cdlInterest} />
          </>
        }
      />
    </LearnerPortalShell>
  );
}
